// Using the same file in google cloud backend
// CHANGES DEPENDING ON DIRECTORY/LIBRARY STRUCTURE:
import { breadcrumb, log } from '@modules/logging';
import {
    collection,
    collectionGroup,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    OrderByDirection,
    QuerySnapshot,
    WhereFilterOp,
    limit,
    query,
    onSnapshot,
    orderBy,
    setDoc,
    where,
    DocumentData,
    DocumentReference,
    addDoc,
    deleteField,
} from 'firebase/firestore';
import { firestore } from './firebaseConfig';

// ^^ CHANGES DEPENDING ON DIRECTORY/LIBRARY STRUCTURE ^^

// we are having issues with circular objects being stringified
// so we'll catch errors here
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function stringify(obj: any, description?: string): string {
    try {
        breadcrumb('stringify-ing type', 'util', { description, type: typeof obj });
        if (obj instanceof Error && obj.message && typeof obj.message === 'string') {
            return obj.message;
        } else {
            return JSON.stringify(obj);
        }
    } catch {
        log(`caught stringify error in: ${description}, type: ${typeof obj}`);
        return '';
    }
}

export const stringFromError = (error: unknown): string => {
    const e = error as ErrorType;
    if (typeof e === 'string') {
        return e;
    } else if (typeof e?.message === 'string') {
        return e.message;
    } else if (typeof e?.statusCode === 'string') {
        return e.statusCode;
    } else {
        return `[stringFromError] unrecognized error object shape, message: ${typeof e?.message}`;
    }
};

export function getSingleObject<Type>(
    collectionPathWithDocId: string,
    description: string,
    docShouldExist?: boolean,
): Promise<Type | false> {
    const data = {
        collectionPathWithDocId,
        description,
        docShouldExist,
    };
    if (collectionPathWithDocId.split('/').length % 2 !== 0) {
        log(`[getSingleObject] missing docId or collection`, data);
        return Promise.resolve(false);
    }
    const ref = doc(firestore, `${collectionPathWithDocId}`);
    return getDoc(ref)
        .then((item) => {
            if (item.exists()) {
                const obj = item.data();

                // add "id" if it doesn't exist
                if (obj && typeof obj === 'object' && !('id' in obj)) {
                    obj.id = item.id;
                }

                return obj as Type;
            } else {
                if (docShouldExist) {
                    log(
                        `No such document: ${description}, collectionPath: ${collectionPathWithDocId}`,
                    );
                }
                return false;
            }
        })
        .catch((error: unknown) => {
            const message = `Error getting document: ${description}, collectionPath: ${collectionPathWithDocId}, Error: ${stringFromError(
                error,
            )}`;
            if (docShouldExist) {
                log(message, data);
            } else {
                breadcrumb(message, `firestore`);
            }

            return false;
        });
}

export function getSingleObjectWhere<Type>(
    collectionPath: string,
    filters: WhereFilter[],
    description = 'no description',
    docShouldExist = true,
): Promise<false | (Type & { id: string })> {
    const whereFilters = filters.map(({ key, operator, value }) => where(key, operator, value));
    const q = query(collection(firestore, collectionPath), ...whereFilters);

    breadcrumb(`getSingleObjectWhere`, 'firestore', {
        collectionPath,
        filters,
        description,
        docShouldExist,
    });

    return getDocs(q)
        .then((querySnapshot) => {
            const noSuchDocument = querySnapshot === null || querySnapshot === undefined;

            if (docShouldExist && noSuchDocument) {
                handleError(`getSingleObjectWhere: ${description}`)(
                    `No such document: ${description}, collectionPath: ${collectionPath}, whereFilter: ${JSON.stringify(
                        filters,
                    )}`,
                );
                return false;
            } else if (noSuchDocument) {
                // doc doesn't exist
                // this is totally fine cause we're just checking for it's existence
                // because we check
                return false;
            } else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const data: any[] = [];
                querySnapshot.forEach((item) => {
                    const obj = item.data();

                    // add "id" if it doesn't exist
                    if (!obj.id) {
                        obj.id = item.id;
                    }

                    data.push(obj);
                });

                const tooFewDocs = data.length === 0;
                const tooManyDocs = data.length > 1;

                if (tooFewDocs || tooManyDocs) {
                    if (tooFewDocs && !docShouldExist) {
                        // doc doesn't exist
                        // this is totally fine cause we're just checking for it's existence
                        // because we check
                        return false;
                    } else {
                        log(
                            `Incorrect getSingleObjectWhere result count: ${
                                data.length
                            } for document: ${description}, collectionPath: ${collectionPath}, whereFilter: ${JSON.stringify(
                                filters,
                            )}`,
                        );
                    }
                }
                return data[0];
            }
        }, handleError(description))
        .catch(handleError(description));
}
export interface WhereFilter {
    key: string;
    operator: WhereFilterOp;
    value: unknown;
}

export interface OrderByParam<Type> {
    key: keyof Type;
    direction: OrderByDirection;
}

function buildQuery<Type>(
    collectionPath: string,
    filters: WhereFilter[] | null,
    orderByParam?: OrderByParam<Type>[],
    limitParam?: number,
) {
    const whereFilters =
        filters?.map(({ key, operator, value }) => where(key, operator, value)) || [];
    const orderBys =
        orderByParam?.map(({ key, direction }) => orderBy(`${String(key)}`, direction)) || [];
    const queryRef = limitParam
        ? query(
              collection(firestore, collectionPath),
              ...whereFilters,
              ...orderBys,
              limit(limitParam),
          )
        : query(collection(firestore, collectionPath), ...whereFilters, ...orderBys);

    return queryRef;
}

function buildCollectionGroupQuery<Type>(
    collectionPath: string,
    filters: WhereFilter[] | null,
    orderByParam?: OrderByParam<Type>,
    limitParam?: number,
) {
    const whereFilters =
        filters?.map(({ key, operator, value }) => where(key, operator, value)) || [];
    const orderBys = orderByParam
        ? [orderBy(`${String(orderByParam.key)}`, orderByParam.direction)]
        : [];
    const queryRef = limitParam
        ? query(
              collectionGroup(firestore, collectionPath),
              ...whereFilters,
              ...orderBys,
              limit(limitParam),
          )
        : query(collectionGroup(firestore, collectionPath), ...whereFilters, ...orderBys);

    return queryRef;
}

function handleSnapshot<Type>(onUpdate: (items: Type[]) => void) {
    return (snap: QuerySnapshot) => {
        const items: Type[] = [];
        if (!snap.empty && snap.docs.length) {
            snap.forEach((item) => {
                items.push({ id: item.id, ...item.data() } as unknown as Type);
            });
        }

        onUpdate(items);
    };
}

export function handleError<Type>(
    description: string,
    resolve?: (value: Type[] | PromiseLike<Type[]>) => void,
) {
    return (e: unknown) => {
        const message = `${description} error: ${stringFromError(e)}`;
        log(message);
        if (resolve) {
            resolve([]);
        } else if (!ON_MOBILE) throw new Error(message);
    };
}

export function getItemsWhere<Type>(
    collectionPath: string,
    description: string,
    filters: WhereFilter[] | null,
    orderByParam?: OrderByParam<Type>[],
    limitParam?: number,
): Promise<Type[]> {
    return new Promise((resolve) => {
        getDocs(buildQuery<Type>(collectionPath, filters, orderByParam, limitParam))
            .then(
                handleSnapshot((i: Type[]) => resolve(i)),
                handleError<Type>(description, resolve),
            )
            .catch((error) => {
                handleError<Type>(`catch-${description}`, resolve)(error);
            });
    });
}

// Watch ANY query
export function onQueryUpdate<Type>(
    collectionPath: string,
    description: string,
    filters: WhereFilter[] | null,
    onUpdate: (items: Type[]) => void,
    orderByParam?: OrderByParam<Type>[],
) {
    return onSnapshot(
        buildQuery<Type>(collectionPath, filters, orderByParam),
        handleSnapshot(onUpdate),
        handleError(description),
    );
}

// Watch ANY collection group query
export function onCollectionGroupQueryUpdate<Type>(
    collectionPath: string,
    description: string,
    filters: WhereFilter[] | null,
    onUpdate: (items: Type[]) => void,
    orderByParam?: OrderByParam<Type>,
) {
    return onSnapshot(
        buildCollectionGroupQuery<Type>(collectionPath, filters, orderByParam),
        handleSnapshot(onUpdate),
        handleError(description),
    );
}

// Watch Claims for the locally signed in user
export function onSingleItemUpdate<Type>(
    docId: string,
    collectionPath: string,
    onUpdate: (item: Type) => void,
    description: string,
): () => void {
    if (docId === undefined || docId.length < 1) {
        const error = `onSingleItemUpdate: ${description} trying to listen to changes without id: ${JSON.stringify(
            docId,
        )}`;

        handleError(`onSingleItemUpdate:${description}`)(error);
        return () => undefined;
    } else {
        const ref = doc(firestore, `${collectionPath}/${docId}`);
        return onSnapshot(
            ref,
            (snap) => {
                const item = snap.data() as Type;
                onUpdate(item);
            },
            handleError(`onSingleItemUpdate${description}`),
        );
    }
}

// function getDocRef<Type>(
//     object: Partial<Type & { id?: string }>,
//     collectionPath:string,
//     subCollection?: { collectionPath:string; docId: string },
// ): { docId: string; ref: DocumentReference } {
//     const existingObj: boolean =
//         'id' in object && !!object?.id && ['string', 'number'].includes(typeof object.id);

//     const collectionRef =
//         subCollection && subCollection.docId && subCollection.collection
//             ? firestore
//                   .collection(collection)
//                   .doc(subCollection.docId)
//                   .collection(subCollection.collection)
//             : firestore.collection(collection);

//     const localDocId = existingObj ? object.id : collectionRef.doc().id;

//     const docId = typeof localDocId !== 'string' ? `${localDocId}` : localDocId;

//     const ref = collectionRef.doc(docId);
//     return { docId, ref };
// }

export function deleteObject(collectionPathWithDocId: string) {
    const ref = doc(firestore, `${collectionPathWithDocId}`);
    return deleteDoc(ref).catch(console.warn);
}

export async function updateObject<Type>(
    object: Partial<Type>,
    collectionPath: string,
    description: string,
): Promise<Type> {
    // const { docId, ref } = getDocRef(object as Type & { id: string }, collection, subCollection);

    const docId =
        'id' in object &&
        object.id &&
        (typeof object.id === 'number' || typeof object.id === 'string')
            ? object.id
            : undefined;

    // delete keys from object
    Object.keys(object).forEach((k) => {
        const key = k as keyof Type;
        if (object[key] === null && typeof key === 'string') {
            object[key] = deleteField() as Type[keyof Type & string];
        }
    });

    const newDocData = { ...object, ...{ id: docId, updatedAt: Date.now() } };

    const existingDocPath = docId ? `/${docId}` : '';
    let ref: DocumentReference<DocumentData, DocumentData> | null;

    if (existingDocPath) {
        ref = doc(firestore, `${collectionPath}${existingDocPath}`);
        await setDoc(ref, newDocData, { merge: true }).catch((e) => log(e));
    } else {
        ref = await addDoc(collection(firestore, collectionPath), newDocData).catch((e) => {
            log(e);
            return null;
        });
        if (ref) {
            await setDoc(ref, { id: ref.id }, { merge: true }).catch((e) => log(e));
        }
    }

    if (ref) {
        const objectRef = await getDoc(ref);
        if (objectRef) {
            return objectRef.data() as Type;
        } else {
            return Promise.reject(
                new Error(
                    `Invalid ${description} info retrieved after update: ${stringify(objectRef)}`,
                ),
            );
        }
    }
    return Promise.reject(new Error(`Invalid ${description} info for update: ${stringify(ref)}`));
}

// function to safely limit where queries: Firebase limits to 10 groups 'in' query
export const createSafeInFilter = (
    key: string,
    list: string[],
    operator: WhereFilter['operator'] = 'in',
): WhereFilter => {
    // Firebase limits to 10 groups 'in' query
    if (list?.length > 10 && typeof list !== 'string') {
        breadcrumb('"in" query limit exceeded', 'firestore', { key, list });
        log(`firestore 'in' filter limit exceeded for '${key}'`);
    }

    const firstTenList = list.slice(0, 10);

    if (firstTenList.length < 1) {
        breadcrumb('"in" query limit missing items', 'firestore', { key, list });
        log(`firestore 'in' has no items for '${key}'`);
        firstTenList.push('null-item');
    }

    return { key, operator, value: firstTenList };
};

const getArchitectureType = (): 'web' | 'mobile' | 'node' => {
    // @ts-ignore shows error in node TS
    if (typeof document !== 'undefined') {
        // I'm on the web!
        return 'web';
        // @ts-ignore shows error in node TS
    } else if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
        // I'm in react-native
        return 'mobile';
    } else {
        // I'm in node js
        return 'node';
    }
};
const ON_MOBILE = getArchitectureType() === 'mobile';

export type ErrorType = string | { message?: string; statusCode?: string };
